import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowClockwise,
  BookOpenText,
  Calculator,
  ChartDonut,
  ChartLineUp,
  CheckCircle,
  CloudCheck,
  FloppyDisk,
  Gauge,
  Moon,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  UsersThree,
  WarningCircle,
  WifiSlash,
} from "@phosphor-icons/react";
import { AcquisitionChart, ChartFrame, CustomerChart, RevenueChart } from "./components/Charts";
import { HelpButton, HelpModal } from "./components/HelpButton";
import { MetricBlock } from "./components/MetricBlock";
import { createGateway, readCache } from "./services/gateway";
import { days, months, multiple, number, percent, yen } from "./lib/format";
import type { DashboardPayload, GlossaryTerm, InputField, ScenarioId } from "./types";

type ViewId = "overview" | "automation" | "diagnostics" | "customers" | "accounting" | "productivity" | "settings" | "glossary";
type Theme = "light" | "dark";

const gateway = createGateway();

const navigation = [
  { id: "overview" as const, label: "全体", icon: ChartLineUp },
  { id: "automation" as const, label: "自動分析", icon: ChartDonut },
  { id: "diagnostics" as const, label: "公的診断", icon: ShieldCheck },
  { id: "customers" as const, label: "顧客", icon: UsersThree },
  { id: "accounting" as const, label: "会計", icon: Calculator },
  { id: "productivity" as const, label: "生産性", icon: Gauge },
  { id: "settings" as const, label: "前提設定", icon: SlidersHorizontal },
  { id: "glossary" as const, label: "用語集", icon: BookOpenText },
];

const sourceLabel = {
  gas: "スプレッドシート接続",
  remote: "PWA接続",
  cache: "オフラインキャッシュ",
  demo: "デモモード",
};

const points = (value: number | null): string => value === null ? "未入力" : `${number(value * 100)}pt`;

export default function App() {
  const [view, setView] = useState<ViewId>("overview");
  const [scenario, setScenario] = useState<ScenarioId>("growth");
  const [payload, setPayload] = useState<DashboardPayload | null>(() => readCache());
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(12);
  const [error, setError] = useState<string | null>(null);
  const [selectedTerm, setSelectedTerm] = useState<GlossaryTerm | null>(null);
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem("management-dashboard:theme") as Theme | null;
    if (saved) return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  const loadDashboard = useCallback(async (nextScenario: ScenarioId, announce = false) => {
    setLoading(true);
    setError(null);
    setLoadingProgress(12);
    const timer = window.setInterval(() => setLoadingProgress((value) => Math.min(92, value + Math.ceil((96 - value) / 4))), 110);
    try {
      const data = await gateway.getDashboard(nextScenario);
      setLoadingProgress(100);
      setPayload(data);
      if (announce) {
        window.setTimeout(() => document.getElementById("page-status")?.focus(), 0);
      }
    } catch (reason) {
      const cached = readCache();
      if (cached) setPayload(cached);
      setError(reason instanceof Error ? reason.message : "データを取得できませんでした。");
    } finally {
      window.clearInterval(timer);
      window.setTimeout(() => setLoading(false), 120);
    }
  }, []);

  useEffect(() => {
    void loadDashboard(scenario);
  }, [loadDashboard, scenario]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("management-dashboard:theme", theme);
  }, [theme]);

  if (!payload && loading) return <LoadingScreen progress={loadingProgress} />;
  if (!payload) return <FatalError message={error ?? "データを表示できません。"} onRetry={() => loadDashboard(scenario)} />;

  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="メインナビゲーション">
        <div className="brand">
          <span className="brand__mark"><ChartLineUp size={22} weight="bold" aria-hidden="true" /></span>
          <div><strong>経営管理</strong><small>Management Console</small></div>
        </div>
        <nav>
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.id} type="button" className={view === item.id ? "nav-item nav-item--active" : "nav-item"} onClick={() => setView(item.id)}>
                <Icon size={20} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar__footer">
          <span className={`source-badge source-badge--${payload.source}`}>{sourceLabel[payload.source]}</span>
          <small>更新 {new Date(payload.updatedAt).toLocaleString("ja-JP", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</small>
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div>
            <p className="topbar__context">営業組織シミュレーション</p>
            <h1>{navigation.find((item) => item.id === view)?.label}</h1>
          </div>
          <div className="topbar__actions">
            <label className="scenario-select">
              <span>表示シナリオ</span>
              <select value={scenario} onChange={(event) => setScenario(event.target.value as ScenarioId)}>
                {payload.scenarios.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select>
            </label>
            <button type="button" className="icon-button" onClick={() => setTheme(theme === "light" ? "dark" : "light")} aria-label={theme === "light" ? "ダークモードに切り替える" : "ライトモードに切り替える"}>
              {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
            </button>
            <button type="button" className="icon-button" onClick={() => loadDashboard(scenario, true)} aria-label="データを再読み込み">
              <ArrowClockwise size={20} className={loading ? "spin" : ""} />
            </button>
          </div>
        </header>

        <div id="page-status" tabIndex={-1} className="status-strip" aria-live="polite">
          <span className={payload.modelStatus === "PASS" ? "status-pass" : "status-warning"}>
            {payload.modelStatus === "PASS" ? <CheckCircle size={17} weight="fill" /> : <WarningCircle size={17} weight="fill" />}
            MODEL {payload.modelStatus}
          </span>
          <span>{loading ? `更新中 ${loadingProgress}%` : error ? "前回取得データを表示中" : payload.scenarioLabel}</span>
          <span className="status-strip__connection">
            {payload.source === "cache" ? <WifiSlash size={17} /> : <CloudCheck size={17} />}
            {sourceLabel[payload.source]}
          </span>
        </div>
        {error && <div className="inline-error"><WarningCircle size={18} />{error}<button onClick={() => loadDashboard(scenario)}>再試行</button></div>}

        {view === "overview" && <Overview payload={payload} onHelp={setSelectedTerm} />}
        {view === "automation" && <Automation payload={payload} onHelp={setSelectedTerm} />}
        {view === "diagnostics" && <Diagnostics payload={payload} onHelp={setSelectedTerm} />}
        {view === "customers" && <Customers payload={payload} onHelp={setSelectedTerm} />}
        {view === "accounting" && <Accounting payload={payload} onHelp={setSelectedTerm} />}
        {view === "productivity" && <Productivity payload={payload} onHelp={setSelectedTerm} />}
        {view === "settings" && <Settings payload={payload} onHelp={setSelectedTerm} onSaved={() => loadDashboard(scenario)} />}
        {view === "glossary" && <Glossary payload={payload} onHelp={setSelectedTerm} />}
      </main>

      <nav className="mobile-nav" aria-label="モバイルナビゲーション">
        {navigation.map((item) => {
          const Icon = item.icon;
          return <button key={item.id} className={view === item.id ? "mobile-nav__item mobile-nav__item--active" : "mobile-nav__item"} onClick={() => setView(item.id)}><Icon size={20} /><span>{item.label}</span></button>;
        })}
      </nav>
      <HelpModal term={selectedTerm} onClose={() => setSelectedTerm(null)} />
    </div>
  );
}

function Diagnostics({ payload, onHelp }: { payload: DashboardPayload; onHelp: (term: GlossaryTerm) => void }) {
  const { localBenchmark, nonFinancial, valueCreation, healthManagement } = payload.publicFramework;
  return <div className="page-stack">
    <div className="callout"><ShieldCheck size={22}/><div><strong>公的な定義と社内の自己評価を分けて表示します</strong><p>財務指標は入力値から自動計算します。非財務4視点と健康経営は社内の準備状況であり、公式の評点・認定・税務判断を代替しません。</p></div></div>
    <section className="metric-band metric-band--four" aria-label="公的フレームワークの主要指標">
      <MetricBlock label="ロカベン財務算出率" value={percent(localBenchmark.completenessRate)} meta="財務6指標" term={payload.glossary.local_benchmark} onHelp={onHelp} tone={localBenchmark.completenessRate === 1 ? "positive" : "warning"} />
      <MetricBlock label="非財務4視点" value={nonFinancial.average === null ? "未入力" : `${number(nonFinancial.average, 1)}/5`} meta="社内自己評価" term={payload.glossary.nonfinancial_maturity} onHelp={onHelp} />
      <MetricBlock label="EP" value={yen(valueCreation.economicProfit, true)} meta="資本コスト控除後" term={payload.glossary.economic_profit} onHelp={onHelp} tone={(valueCreation.economicProfit ?? 0) > 0 ? "positive" : "default"} />
      <MetricBlock label="健康経営の準備率" value={percent(healthManagement.readinessRate)} meta={`${healthManagement.checksMet}/${healthManagement.checksTotal}区分`} term={payload.glossary.health_management_readiness} onHelp={onHelp} tone={healthManagement.readinessRate === 1 ? "positive" : "warning"} />
    </section>

    <div className="analysis-grid analysis-grid--automation">
      <IndicatorList title="ローカルベンチマーク財務6指標" rows={[
        ["売上増加率", percent(localBenchmark.salesGrowthRate), "local_sales_growth"],
        ["営業利益率", percent(localBenchmark.operatingMargin), "operating_profit"],
        ["労働生産性", yen(localBenchmark.laborProductivity), "local_labor_productivity"],
        ["EBITDA有利子負債倍率", multiple(localBenchmark.ebitdaDebtMultiple), "ebitda_debt_multiple"],
        ["営業運転資本回転期間", months(localBenchmark.workingCapitalMonths), "operating_working_capital_months"],
        ["自己資本比率", percent(localBenchmark.equityRatio), "equity_ratio"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="ローカルベンチマーク非財務4視点" rows={[
        ["経営者", `${number(nonFinancial.management, 1)}/5`, "nonfinancial_maturity"],
        ["事業", `${number(nonFinancial.business, 1)}/5`, "nonfinancial_maturity"],
        ["環境・関係者", `${number(nonFinancial.stakeholders, 1)}/5`, "nonfinancial_maturity"],
        ["内部管理体制", `${number(nonFinancial.internalControl, 1)}/5`, "nonfinancial_maturity"],
        ["4視点平均", nonFinancial.average === null ? "未入力" : `${number(nonFinancial.average, 1)}/5`, "nonfinancial_maturity"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="企業価値と成長投資" rows={[
        ["NOPAT", yen(valueCreation.nopat), "nopat"],
        ["投下資本", yen(valueCreation.investedCapital), "roic"],
        ["ROIC", percent(valueCreation.roic), "roic"],
        ["WACC", percent(valueCreation.wacc), "wacc"],
        ["ROIC-WACCスプレッド", points(valueCreation.roicWaccSpread), "roic_wacc_spread"],
        ["EP", yen(valueCreation.economicProfit), "economic_profit"],
        ["成長投資合計", yen(valueCreation.growthInvestment), "growth_investment_rate"],
        ["成長投資率", percent(valueCreation.growthInvestmentRate), "growth_investment_rate"],
        ["人的資本投資/FTE", yen(valueCreation.humanInvestmentPerFte), "human_capital_investment"],
        ["研修時間/FTE", valueCreation.trainingHoursPerFte === null ? "未入力" : `${number(valueCreation.trainingHoursPerFte)}時間`, "human_capital_investment"],
        ["設備投資/減価償却", multiple(valueCreation.capexDepreciation), "capex_depreciation"],
        ["賃金増加率", percent(valueCreation.wageGrowthRate), "human_capital_investment"],
        ["自主都合離職率", percent(valueCreation.turnoverRate), "voluntary_turnover"],
        ["従業員エンゲージメント", `${number(valueCreation.engagementScore, 1)}点`, "engagement_score"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="健康経営の成果指標" rows={[
        ["定期健診受診率", percent(healthManagement.healthCheckRate), "health_check_rate"],
        ["ストレスチェック実施率", percent(healthManagement.stressCheckRate), "health_management_readiness"],
        ["年次有給休暇取得率", percent(healthManagement.paidLeaveRate), "health_management_readiness"],
        ["月平均法定外労働時間", `${number(healthManagement.overtimeHours, 1)}時間`, "health_management_readiness"],
        ["健康投資/FTE", yen(healthManagement.healthInvestmentPerFte), "health_investment"],
        ["準備ステータス", healthManagement.status, "health_management_readiness"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
    </div>

    <section className="readiness-panel">
      <header><div><h2>健康経営・主要区分チェック</h2><p>健康経営優良法人2026の公開要件を基にした社内確認。申請時は対象部門・企業規模・申請年度の正式要件を再確認してください。</p></div><strong>{healthManagement.checksMet}/{healthManagement.checksTotal}</strong></header>
      <div className="readiness-grid">{healthManagement.checks.map((check) => <div className={check.met ? "readiness-item readiness-item--met" : "readiness-item"} key={check.id}><span aria-hidden="true">{check.met ? <CheckCircle size={18} weight="fill" /> : <WarningCircle size={18} />}</span><div><strong>{check.label}</strong><p>{check.requirement}</p></div></div>)}</div>
    </section>

    <section className="source-panel"><header><h2>公式参照先</h2><p>基準改定時はリンク先を正として更新します。</p></header><div>
      <a href="https://www.meti.go.jp/policy/economy/keiei_innovation/sangyokinyu/locaben/sheet.html" target="_blank" rel="noreferrer">経済産業省 ローカルベンチマーク</a>
      <a href="https://www.nichizeiren.or.jp/taxaccount/sme_support/guide/" target="_blank" rel="noreferrer">日本税理士会連合会 中小企業の会計</a>
      <a href="https://www.meti.go.jp/shingikai/sankoshin/shin_kijiku/value_creation/index.html" target="_blank" rel="noreferrer">経済産業省 企業価値・成長投資</a>
      <a href="https://www.meti.go.jp/policy/mono_info_service/healthcare/kenkoukeiei_yuryouhouzin.html" target="_blank" rel="noreferrer">経済産業省 健康経営優良法人制度</a>
    </div></section>
  </div>;
}

function Automation({ payload, onHelp }: { payload: DashboardPayload; onHelp: (term: GlossaryTerm) => void }) {
  const { growth, profitability, unitEconomics, cashFlow, variance } = payload.automation;
  const missingInputs = payload.inputs.filter((item) => [
    "b2bAcquisitionCost",
    "b2cAcquisitionCost",
    "cash",
    "interestBearingDebt",
    "annualCapex",
    "annualPrincipalRepayment",
  ].includes(item.key) && item.value === 0);
  const firstProfit = profitability.firstProfitableMonth === null ? "未入力" : `${profitability.firstProfitableMonth}月`;

  return <div className="page-stack">
    {missingInputs.length > 0 && <div className="callout"><ChartDonut size={22}/><div><strong>入力を増やすと自動計算が広がります</strong><p>顧客獲得費、現預金、借入、設備投資、返済額を前提設定へ入力すると、CAC、LTV/CAC、FCF、DSCRまで自動表示されます。</p></div></div>}
    <section className="metric-band metric-band--four" aria-label="自動計算の主要指標">
      <MetricBlock label="ARR" value={yen(growth.arr, true)} meta="年末MRRの年換算" term={payload.glossary.arr} onHelp={onHelp} />
      <MetricBlock label="Rule of 40" value={points(growth.ruleOf40)} meta="成長率 + 営業利益率" term={payload.glossary.rule_of_40} onHelp={onHelp} tone={(growth.ruleOf40 ?? 0) >= 0.4 ? "positive" : "warning"} />
      <MetricBlock label="BtoB LTV/CAC" value={multiple(unitEconomics.b2bLtvCac)} meta="顧客獲得投資の採算" term={payload.glossary.ltv_cac} onHelp={onHelp} tone={(unitEconomics.b2bLtvCac ?? 0) >= 3 ? "positive" : "default"} />
      <MetricBlock label="FCF概算" value={yen(cashFlow.freeCashFlowProxy, true)} meta="営業CF - 設備投資" term={payload.glossary.fcf_proxy} onHelp={onHelp} tone={cashFlow.freeCashFlowProxy >= 0 ? "positive" : "warning"} />
    </section>

    <div className="analysis-grid analysis-grid--automation">
      <IndicatorList title="成長とストック" rows={[
        ["ARR", yen(growth.arr), "arr"],
        ["年商ランレート", yen(growth.revenueRunRate), "revenue_run_rate"],
        ["ストック売上比率", percent(growth.recurringRevenueRate), "recurring_revenue_ratio"],
        ["MRR年換算成長率", percent(growth.annualizedMrrGrowth), "mrr_growth"],
        ["平均月次売上成長率", percent(growth.averageMonthlyRevenueGrowth), "revenue"],
        ["Rule of 40", points(growth.ruleOf40), "rule_of_40"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="採算と黒字化" rows={[
        ["EBITDA", yen(payload.accounting.ebitda), "ebitda"],
        ["EBITDA率", percent(profitability.ebitdaMargin), "ebitda_margin"],
        ["限界利益率", percent(profitability.contributionMarginRate), "contribution_margin"],
        ["営業レバレッジ", multiple(profitability.operatingLeverage), "operating_leverage"],
        ["損益分岐点売上高", yen(profitability.breakEvenSales), "break_even_sales"],
        ["安全余裕率", percent(profitability.marginOfSafetyRate), "margin_of_safety"],
        ["初回黒字化月", firstProfit, "operating_profit"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="BtoB顧客効率" rows={[
        ["ARPU", yen(unitEconomics.b2bArpu), "arpu"],
        ["想定継続月数", months(unitEconomics.b2bLifetimeMonths), "ltv"],
        ["LTV", yen(unitEconomics.b2bLtv), "ltv"],
        ["CAC", yen(unitEconomics.b2bCac), "cac"],
        ["LTV/CAC", multiple(unitEconomics.b2bLtvCac), "ltv_cac"],
        ["CAC回収月数", months(unitEconomics.b2bPaybackMonths), "cac_payback"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="BtoC顧客効率" rows={[
        ["ARPU", yen(unitEconomics.b2cArpu), "arpu"],
        ["想定継続月数", months(unitEconomics.b2cLifetimeMonths), "ltv"],
        ["LTV", yen(unitEconomics.b2cLtv), "ltv"],
        ["CAC", yen(unitEconomics.b2cCac), "cac"],
        ["LTV/CAC", multiple(unitEconomics.b2cLtvCac), "ltv_cac"],
        ["CAC回収月数", months(unitEconomics.b2cPaybackMonths), "cac_payback"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="資金繰りと借入" rows={[
        ["運転資金増減", yen(cashFlow.workingCapitalChange), "working_capital_change"],
        ["営業CF概算", yen(cashFlow.operatingCashFlowProxy), "ocf_proxy"],
        ["FCF概算", yen(cashFlow.freeCashFlowProxy), "fcf_proxy"],
        ["DSCR", multiple(cashFlow.dscr), "dscr"],
        ["ネット有利子負債/EBITDA", multiple(cashFlow.netDebtToEbitda), "net_debt_ebitda"],
        ["ROIC", percent(cashFlow.roic), "roic"],
        ["手元流動性日数", days(cashFlow.cashDays), "cash_days"],
        ["キャッシュコンバージョン", percent(cashFlow.cashConversionRate), "cash_conversion"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="現在実績の計画達成率" rows={[
        ["売上高", percent(variance.revenueAttainment), "budget_attainment"],
        ["営業利益", percent(variance.operatingProfitAttainment), "budget_attainment"],
        ["EBITDA", percent(variance.ebitdaAttainment), "budget_attainment"],
        ["BtoB年間新規", percent(variance.b2bNewAttainment), "budget_attainment"],
        ["BtoC年間新規", percent(variance.b2cNewAttainment), "budget_attainment"],
        ["年末稼働契約数", percent(variance.yearEndActiveAttainment), "budget_attainment"],
        ["年末MRR", percent(variance.mrrAttainment), "budget_attainment"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
    </div>
  </div>;
}

function Overview({ payload, onHelp }: { payload: DashboardPayload; onHelp: (term: GlossaryTerm) => void }) {
  const last = payload.monthly.at(-1);
  const previous = payload.monthly.at(-2);
  const revenueChange = last && previous && previous.revenue ? ((last.revenue / previous.revenue) - 1) * 100 : null;
  return (
    <div className="page-stack">
      <section className="metric-band" aria-label="主要経営指標">
        <MetricBlock label="年間売上高" value={yen(payload.summary.revenue, true)} change={revenueChange} term={payload.glossary.revenue} onHelp={onHelp} />
        <MetricBlock label="粗利益" value={yen(payload.summary.grossProfit, true)} meta={percent(payload.summary.grossMargin)} term={payload.glossary.gross_profit} onHelp={onHelp} />
        <MetricBlock label="営業利益" value={yen(payload.summary.operatingProfit, true)} meta={percent(payload.summary.operatingMargin)} term={payload.glossary.operating_profit} onHelp={onHelp} tone="positive" />
        <MetricBlock label="年末MRR" value={yen(payload.summary.mrr, true)} meta="BtoB + BtoC" term={payload.glossary.mrr} onHelp={onHelp} />
        <MetricBlock label="年末稼働契約数" value={`${number(payload.summary.yearEndActive)}件`} meta="BtoB + BtoC" term={payload.glossary.active_contracts} onHelp={onHelp} />
      </section>

      <div className="chart-grid chart-grid--primary">
        <ChartFrame title="月次売上と営業利益" description="成長の速度と利益への転換を同時に確認" term={payload.glossary.revenue} onHelp={onHelp} wide><RevenueChart data={payload.monthly} /></ChartFrame>
        <section className="decision-panel">
          <header><div><h2>経営判断サマリー</h2><p>今月確認する主要な論点</p></div></header>
          <DecisionRow label="粗利率" value={percent(payload.summary.grossMargin)} note="提供原価を反映" tone="good" />
          <DecisionRow label="営業利益率" value={percent(payload.summary.operatingMargin)} note="固定費入力後に再確認" tone="good" />
          <DecisionRow label="安全余裕率" value={percent(payload.accounting.marginOfSafetyRate)} note="損益分岐点との差" tone={payload.accounting.marginOfSafetyRate >= 0.2 ? "good" : "warn"} />
          <DecisionRow label="BtoB目標" value={percent(payload.summary.b2bTargetRate)} note="年間新規獲得" tone={payload.summary.b2bTargetRate >= 1 ? "good" : "warn"} />
          <DecisionRow label="BtoC目標" value={percent(payload.summary.b2cTargetRate)} note="年間新規獲得" tone={payload.summary.b2cTargetRate >= 1 ? "good" : "warn"} />
        </section>
      </div>

      <div className="chart-grid">
        <ChartFrame title="稼働契約数" description="BtoBとBtoCの月末稼働契約" term={payload.glossary.active_contracts} onHelp={onHelp}><CustomerChart data={payload.monthly} /></ChartFrame>
        <ChartFrame title="新規獲得と解約" description="顧客基盤の増減要因" term={payload.glossary.churn_rate} onHelp={onHelp}><AcquisitionChart data={payload.monthly} /></ChartFrame>
      </div>
    </div>
  );
}

function DecisionRow({ label, value, note, tone }: { label: string; value: string; note: string; tone: "good" | "warn" }) {
  return <div className="decision-row"><div><strong>{label}</strong><span>{note}</span></div><b className={tone === "good" ? "decision-good" : "decision-warn"}>{value}</b></div>;
}

function Customers({ payload, onHelp }: { payload: DashboardPayload; onHelp: (term: GlossaryTerm) => void }) {
  const totalB2bNew = payload.monthly.reduce((sum, row) => sum + row.b2bNew, 0);
  const totalB2cNew = payload.monthly.reduce((sum, row) => sum + row.b2cNew, 0);
  const totalB2bChurn = payload.monthly.reduce((sum, row) => sum + row.b2bChurn, 0);
  const totalB2cChurn = payload.monthly.reduce((sum, row) => sum + row.b2cChurn, 0);
  return <div className="page-stack">
    <section className="metric-band metric-band--four">
      <MetricBlock label="BtoB年間新規" value={`${number(totalB2bNew)}件`} meta={percent(payload.summary.b2bTargetRate)} term={payload.glossary.active_contracts} onHelp={onHelp} />
      <MetricBlock label="BtoC年間新規" value={`${number(totalB2cNew)}件`} meta={percent(payload.summary.b2cTargetRate)} term={payload.glossary.active_contracts} onHelp={onHelp} />
      <MetricBlock label="BtoB年間解約" value={`${number(totalB2bChurn)}件`} term={payload.glossary.churn_rate} onHelp={onHelp} tone="warning" />
      <MetricBlock label="BtoC年間解約" value={`${number(totalB2cChurn)}件`} term={payload.glossary.churn_rate} onHelp={onHelp} tone="warning" />
    </section>
    <div className="chart-grid">
      <ChartFrame title="稼働契約数の推移" description="継続課金売上の顧客基盤" term={payload.glossary.active_contracts} onHelp={onHelp}><CustomerChart data={payload.monthly} /></ChartFrame>
      <ChartFrame title="新規獲得と解約" description="月ごとの純増構造" term={payload.glossary.net_adds} onHelp={onHelp}><AcquisitionChart data={payload.monthly} /></ChartFrame>
    </div>
    <MonthlyTable payload={payload} />
  </div>;
}

function MonthlyTable({ payload }: { payload: DashboardPayload }) {
  return <section className="data-section"><header><div><h2>月次契約明細</h2><p>新規、解約、稼働数を月単位で確認</p></div></header><div className="table-scroll"><table><thead><tr><th>月</th><th>BtoB新規</th><th>BtoC新規</th><th>BtoB解約</th><th>BtoC解約</th><th>BtoB稼働</th><th>BtoC稼働</th></tr></thead><tbody>{payload.monthly.map((row)=><tr key={row.month}><td>{row.month}月</td><td>{number(row.b2bNew)}</td><td>{number(row.b2cNew)}</td><td>{number(row.b2bChurn)}</td><td>{number(row.b2cChurn)}</td><td>{number(row.b2bActive)}</td><td>{number(row.b2cActive)}</td></tr>)}</tbody></table></div></section>;
}

function Accounting({ payload, onHelp }: { payload: DashboardPayload; onHelp: (term: GlossaryTerm) => void }) {
  const rows = [
    ["限界利益", yen(payload.accounting.contributionMargin), "contribution_margin"],
    ["限界利益率", percent(payload.accounting.contributionMarginRate), "contribution_margin"],
    ["EBITDA", yen(payload.accounting.ebitda), "ebitda"],
    ["経常利益", yen(payload.accounting.ordinaryProfit), "ordinary_profit"],
    ["税引前利益", yen(payload.accounting.pretaxProfit), "pretax_profit"],
    ["当期純利益", yen(payload.accounting.netProfit), "net_profit"],
    ["損益分岐点売上高", yen(payload.accounting.breakEvenSales), "break_even_sales"],
    ["安全余裕率", percent(payload.accounting.marginOfSafetyRate), "margin_of_safety"],
  ] as const;
  const capitalRows = [
    ["ROA", percent(payload.accounting.roa), "roa"], ["ROE", percent(payload.accounting.roe), "roe"],
    ["自己資本比率", percent(payload.accounting.equityRatio), "equity_ratio"], ["D/Eレシオ", multiple(payload.accounting.deRatio), "de_ratio"],
    ["利払い余力", multiple(payload.accounting.interestCoverage), "interest_coverage"], ["現預金月商比", months(payload.accounting.cashMonths), "cash_months"],
    ["固定費ランウェイ", months(payload.accounting.runway), "runway"], ["流動比率", percent(payload.accounting.currentRatio), "current_ratio"],
    ["当座比率", percent(payload.accounting.quickRatio), "quick_ratio"],
  ] as const;
  return <div className="page-stack">
    <section className="metric-band metric-band--four">
      <MetricBlock label="EBITDA" value={yen(payload.accounting.ebitda, true)} term={payload.glossary.ebitda} onHelp={onHelp} />
      <MetricBlock label="当期純利益" value={yen(payload.accounting.netProfit, true)} term={payload.glossary.net_profit} onHelp={onHelp} />
      <MetricBlock label="損益分岐点" value={yen(payload.accounting.breakEvenSales, true)} term={payload.glossary.break_even_sales} onHelp={onHelp} />
      <MetricBlock label="安全余裕率" value={percent(payload.accounting.marginOfSafetyRate)} term={payload.glossary.margin_of_safety} onHelp={onHelp} tone={payload.accounting.marginOfSafetyRate >= 0.2 ? "positive" : "warning"} />
    </section>
    <div className="analysis-grid">
      <IndicatorList title="利益と損益分岐" rows={rows} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="資本効率と財務安全性" rows={capitalRows} glossary={payload.glossary} onHelp={onHelp} />
      <IndicatorList title="運転資金" rows={[
        ["売上債権回転日数", days(payload.workingCapital.dso), "dso"], ["棚卸資産回転日数", days(payload.workingCapital.dio), "dio"],
        ["仕入債務回転日数", days(payload.workingCapital.dpo), "dpo"], ["CCC", days(payload.workingCapital.ccc), "ccc"],
        ["営業運転資金", yen(payload.workingCapital.workingCapital), "working_capital"], ["総資産回転率", multiple(payload.workingCapital.assetTurnover), "asset_turnover"],
        ["簡易CAC", yen(payload.workingCapital.cac), "cac"], ["ネット有利子負債", yen(payload.workingCapital.netDebt), "net_debt"],
      ]} glossary={payload.glossary} onHelp={onHelp} />
    </div>
  </div>;
}

function IndicatorList({ title, rows, glossary, onHelp }: { title: string; rows: readonly (readonly [string, string, string])[]; glossary: DashboardPayload["glossary"]; onHelp: (term: GlossaryTerm) => void }) {
  return <section className="indicator-list"><header><h2>{title}</h2></header><div>{rows.map(([label, value, helpId])=><div className="indicator-row" key={label}><span>{label}<HelpButton term={glossary[helpId]} onOpen={onHelp} /></span><strong className={value === "未入力" ? "value-empty" : ""}>{value}</strong></div>)}</div></section>;
}

function Productivity({ payload, onHelp }: { payload: DashboardPayload; onHelp: (term: GlossaryTerm) => void }) {
  const values = [
    ["売上高／総人時", yen(payload.productivity.revenuePerHour), "labor_productivity"],
    ["粗利益／総人時", yen(payload.productivity.grossProfitPerHour), "labor_productivity"],
    ["EBITDA／総人時", yen(payload.productivity.ebitdaPerHour), "labor_productivity"],
    ["売上高／FTE", yen(payload.productivity.revenuePerFte), "revenue_per_fte"],
    ["粗利益／FTE", yen(payload.productivity.grossProfitPerFte), "revenue_per_fte"],
    ["EBITDA／FTE", yen(payload.productivity.ebitdaPerFte), "revenue_per_fte"],
    ["社内人件費率", percent(payload.productivity.laborCostRatio), "labor_cost_ratio"],
    ["労働分配率", percent(payload.productivity.laborDistributionRate), "labor_distribution"],
  ] as const;
  const missing = payload.inputs.filter((item) => ["fte", "annualHours"].includes(item.key) && item.value === 0);
  return <div className="page-stack">
    {missing.length > 0 && <div className="callout"><Gauge size={22}/><div><strong>生産性の入力が必要です</strong><p>前提設定で社内稼働人数と年間総人時を入力すると、人時生産性とFTE当たり指標が表示されます。</p></div></div>}
    <section className="metric-band metric-band--four">
      <MetricBlock label="粗利益／総人時" value={yen(payload.productivity.grossProfitPerHour)} term={payload.glossary.labor_productivity} onHelp={onHelp} />
      <MetricBlock label="EBITDA／総人時" value={yen(payload.productivity.ebitdaPerHour)} term={payload.glossary.labor_productivity} onHelp={onHelp} />
      <MetricBlock label="売上高／FTE" value={yen(payload.productivity.revenuePerFte, true)} term={payload.glossary.revenue_per_fte} onHelp={onHelp} />
      <MetricBlock label="労働分配率" value={percent(payload.productivity.laborDistributionRate)} term={payload.glossary.labor_distribution} onHelp={onHelp} />
    </section>
    <IndicatorList title="人時・人員生産性" rows={values} glossary={payload.glossary} onHelp={onHelp} />
  </div>;
}

function Settings({ payload, onHelp, onSaved }: { payload: DashboardPayload; onHelp: (term: GlossaryTerm) => void; onSaved: () => void }) {
  const [values, setValues] = useState<Record<string, number>>(() => Object.fromEntries(payload.inputs.map((item) => [item.key, item.value])));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const groups = useMemo(() => payload.inputs.reduce<Record<string, InputField[]>>((result, item) => {
    (result[item.group] ??= []).push(item);
    return result;
  }, {}), [payload.inputs]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true); setMessage(null);
    try {
      await gateway.updateInputs(values);
      setMessage(payload.source === "demo" ? "デモモード内で更新しました。" : "スプレッドシートを更新しました。");
      onSaved();
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "更新できませんでした。");
    } finally { setSaving(false); }
  };
  return <form className="settings-form" onSubmit={submit}>
    <header className="settings-form__header"><div><h2>前提設定</h2><p>変更内容は保存後、すべての指標へ反映されます。</p></div><button type="submit" className="primary-button" disabled={saving}><FloppyDisk size={18}/>{saving ? "保存中" : "保存"}</button></header>
    {message && <div className="save-message" aria-live="polite">{message}</div>}
    {Object.entries(groups).map(([group, fields]) => <section className="settings-group" key={group}><h3>{group}</h3><div className="settings-grid">{fields?.map((field)=><SettingField key={field.key} field={field} value={values[field.key] ?? 0} term={field.helpId ? payload.glossary[field.helpId] : undefined} onHelp={onHelp} onChange={(value)=>setValues((current)=>({...current,[field.key]:value}))}/>)}</div></section>)}
  </form>;
}

function SettingField({ field, value, term, onHelp, onChange }: { field: InputField; value: number; term?: GlossaryTerm; onHelp: (term: GlossaryTerm) => void; onChange: (value: number) => void }) {
  const isPercent = field.unit.includes("%");
  const scale = isPercent ? 100 : 1;
  const displayValue = value * scale;
  const displayStep = (field.step ?? (isPercent ? 0.001 : 1)) * scale;
  const displayMax = field.max === undefined ? undefined : field.max * scale;
  return <label className="setting-field"><span>{field.label}<HelpButton term={term} onOpen={onHelp}/></span><div className="setting-field__control"><input type="number" min="0" max={displayMax} step={displayStep} value={displayValue} onChange={(event)=>onChange((Number(event.target.value) || 0) / scale)}/><em>{field.unit}</em></div></label>;
}

function Glossary({ payload, onHelp }: { payload: DashboardPayload; onHelp: (term: GlossaryTerm) => void }) {
  const [query, setQuery] = useState("");
  const terms = Object.values(payload.glossary).filter((term) => `${term.displayName}${term.category}${term.shortDescription}`.toLowerCase().includes(query.toLowerCase()));
  return <div className="glossary-page"><header><div><h2>経営用語集</h2><p>スプレッドシートの「用語集」タブと連動します。</p></div><input type="search" value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="用語を検索" aria-label="用語を検索" /></header><div className="glossary-grid">{terms.map((term)=><button type="button" key={term.id} className="glossary-item" onClick={()=>onHelp(term)}><span>{term.category}</span><strong>{term.displayName}</strong><p>{term.shortDescription}</p></button>)}</div>{terms.length===0&&<div className="empty-state"><BookOpenText size={34}/><strong>該当する用語がありません</strong><p>検索条件を短くしてお試しください。</p></div>}</div>;
}

function LoadingScreen({ progress }: { progress: number }) {
  return <div className="loading-screen" aria-live="polite"><div className="loading-screen__mark"><ChartLineUp size={28} weight="bold" /></div><strong>経営データを読み込んでいます</strong><span>{progress}%</span><div className="loading-track"><i style={{ width: `${progress}%` }} /></div></div>;
}

function FatalError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div className="fatal-error"><WarningCircle size={36}/><h1>データを表示できません</h1><p>{message}</p><button className="primary-button" onClick={onRetry}>再試行</button></div>;
}
